import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { AdminService } from '../../../services/admin/admin.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-support-manager',
  templateUrl: './support-manager.component.html',
  styleUrls: ['./support-manager.component.scss']
})
export class SupportManagerComponent implements OnInit {
  @ViewChild('messagesEnd') messagesEnd!: ElementRef;

  tickets: any[] = [];
  loading = false;
  activeTab = 'ALL';
  tabs = ['ALL', 'New', 'In Progress', 'Waiting for User', 'Resolved', 'Closed'];

  selectedTicket: any = null;
  loadingThread = false;

  // Reply
  replyText = '';
  isInternalNote = false;
  sendingReply = false;

  // Status / Priority change
  updatingStatus = false;
  updatingPriority = false;

  statuses = ['New', 'In Progress', 'Waiting for User', 'Resolved', 'Closed'];
  priorities = ['Low', 'Medium', 'High', 'Urgent'];

  constructor(private adminService: AdminService) {}

  ngOnInit(): void {
    this.loadTickets();
  }

  loadTickets(): void {
    this.loading = true;
    const filters: any = {};
    if (this.activeTab !== 'ALL') filters.status = this.activeTab;

    this.adminService.adminGetTickets(filters).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.status === 'success') this.tickets = res.data || [];
      },
      error: () => { this.loading = false; }
    });
  }

  getTabCount(tab: string): number {
    if (tab === 'ALL') return this.tickets.length;
    return this.tickets.filter(t => t.status === tab).length;
  }

  get filteredTickets(): any[] {
    if (this.activeTab === 'ALL') return this.tickets;
    return this.tickets.filter(t => t.status === this.activeTab);
  }

  selectTab(tab: string): void {
    this.activeTab = tab;
    this.selectedTicket = null;
    this.loadTickets();
  }

  openTicket(ticket: any): void {
    this.selectedTicket = ticket;
    this.replyText = '';
    this.isInternalNote = false;
    this.loadingThread = true;
    this.adminService.adminGetTicketById(ticket.ticket_id).subscribe({
      next: (res) => {
        this.loadingThread = false;
        if (res.status === 'success') {
          this.selectedTicket = res.data;
          setTimeout(() => this.scrollToBottom(), 100);
        }
      },
      error: () => { this.loadingThread = false; }
    });
  }

  sendReply(): void {
    if (!this.replyText.trim() || this.sendingReply) return;
    this.sendingReply = true;
    this.adminService.adminReplyTicket(this.selectedTicket.ticket_id, this.replyText.trim(), this.isInternalNote).subscribe({
      next: (res) => {
        this.sendingReply = false;
        if (res.status === 'success') {
          const msg = res.data;
          this.selectedTicket.messages = [...(this.selectedTicket.messages || []), msg];
          if (!this.isInternalNote) {
            this.selectedTicket.status = 'Waiting for User';
            const idx = this.tickets.findIndex(t => t.ticket_id === this.selectedTicket.ticket_id);
            if (idx !== -1) this.tickets[idx].status = 'Waiting for User';
          }
          this.replyText = '';
          this.isInternalNote = false;
          setTimeout(() => this.scrollToBottom(), 80);
        }
      },
      error: (err) => {
        this.sendingReply = false;
        Swal.fire({ title: 'Error', text: err.error?.message || 'Failed to send reply', icon: 'error', confirmButtonColor: '#B01E3E', didOpen: () => { const c = document.querySelector('.swal2-container') as HTMLElement; if (c) c.style.zIndex = '99999'; } });
      }
    });
  }

  updateStatus(status: string): void {
    if (this.updatingStatus || !status) return;
    this.updatingStatus = true;
    this.adminService.adminUpdateTicketStatus(this.selectedTicket.ticket_id, status).subscribe({
      next: () => {
        this.updatingStatus = false;
        this.selectedTicket.status = status;
        const idx = this.tickets.findIndex(t => t.ticket_id === this.selectedTicket.ticket_id);
        if (idx !== -1) this.tickets[idx].status = status;
      },
      error: () => { this.updatingStatus = false; }
    });
  }

  updatePriority(priority: string): void {
    if (this.updatingPriority || !priority) return;
    this.updatingPriority = true;
    this.adminService.adminUpdateTicketPriority(this.selectedTicket.ticket_id, priority).subscribe({
      next: () => {
        this.updatingPriority = false;
        this.selectedTicket.priority = priority;
        const idx = this.tickets.findIndex(t => t.ticket_id === this.selectedTicket.ticket_id);
        if (idx !== -1) this.tickets[idx].priority = priority;
      },
      error: () => { this.updatingPriority = false; }
    });
  }

  closeSelected(): void {
    this.selectedTicket = null;
    this.replyText = '';
  }

  scrollToBottom(): void {
    try { this.messagesEnd?.nativeElement?.scrollIntoView({ behavior: 'smooth' }); } catch (_) {}
  }

  getStatusClass(status: string): string {
    const m: any = { 'New': 'badge-new', 'In Progress': 'badge-progress', 'Waiting for User': 'badge-waiting', 'Resolved': 'badge-resolved', 'Closed': 'badge-closed' };
    return m[status] || 'badge-new';
  }

  getPriorityClass(p: string): string {
    const m: any = { 'Low': 'pri-low', 'Medium': 'pri-medium', 'High': 'pri-high', 'Urgent': 'pri-urgent' };
    return m[p] || 'pri-medium';
  }

  getCategoryIcon(cat: string): string {
    const m: any = { 'Payment': 'payments', 'Profile': 'manage_accounts', 'Verification': 'verified_user', 'Technical Issue': 'build', 'Broker': 'handshake', 'Other': 'help' };
    return m[cat] || 'help';
  }

  getUserName(user: any): string {
    if (!user) return 'Unknown';
    return `${user.basic_info?.first_name || ''} ${user.basic_info?.last_name || ''}`.trim() || 'Unknown';
  }

  getUserEmail(user: any): string {
    return user?.basic_info?.email || 'N/A';
  }

  getUserMobile(user: any): string {
    return user?.basic_info?.mobile || 'N/A';
  }

  trackByMsg(_: number, msg: any): string { return msg._id || msg.created_at; }
}
